import { colors } from '../../../theme/color-palette';
import { INLINE_BLOCK } from '../../../constants/constant';
import upArrowIcon from '../../../assets/dashboard-svg/up-arrow.svg';
import downArrowIcon from '../../../assets/dashboard-svg/down-arrow.svg';
import { convertToInternationalCurrencySystem } from '../../../helper/convertAmount';

const { RGB_POSITIVE_COLORS, RGB_NEGATIVE_COLORS } = colors.HEATMAP;

export const formatNumberWithUnits = (
	number,
	prefix = '',
	suffix = '',
	isLegend
) => {
	if (typeof number !== 'number' || [-999999, 999999].includes(number)) {
		return isLegend ? '-' : 'NA';
	}
	const isPositive = number >= 0;
	const result = isPositive
		? `${prefix}${convertToInternationalCurrencySystem(
				Math.abs(number)
		  )}${suffix}`
		: `-${prefix}${convertToInternationalCurrencySystem(
				Math.abs(number)
		  )}${suffix}`;
	return result;
};

export const onHover = ({ x, y }) => {
	if (window.mapGL.map.mapOverlay._deck.tooltip) {
		const element = window.mapGL.map.mapOverlay._deck.tooltip.el;
		element.style.left = x > 150 ? '-150px' : '0px';
		element.style.top = y > 110 ? '-100px' : '0px';
	}
};

export const getColorSlotMap = (POSITIVE_SLOTS, NEGATIVE_SLOTS) => {
	let colorSlotMap = {};

	if (POSITIVE_SLOTS.length > 0) {
		POSITIVE_SLOTS.forEach((e, index) => {
			colorSlotMap[RGB_POSITIVE_COLORS[index]] = e;
		});
	}
	if (NEGATIVE_SLOTS.length > 0) {
		NEGATIVE_SLOTS.forEach((e, index) => {
			colorSlotMap[RGB_NEGATIVE_COLORS[index]] = e;
		});
	}
	return colorSlotMap;
};

export const fillColor = (colorSlotMap, selectKey = 'yoyValue') => {
	return (f) => {
		try {
			let color = [144, 145, 150];
			const value = f.properties[selectKey];
			for (const key in colorSlotMap) {
				if (Object.hasOwnProperty.call(colorSlotMap, key)) {
					const colorSlot = colorSlotMap[key];
					if (value >= colorSlot[0] && value <= colorSlot[1]) {
						color = key.split(',')?.map(Number);
					}
				}
			}

			return color;
		} catch (error) {
			return [144, 145, 150];
		}
	};
};

export const getLegendSlots = (min, max) => {
	const POSITIVE_SLOTS = [];
	const NEGATIVE_SLOTS = [];
	const BUCKETS_SIZE = 3;

	if (min < 0) {
		const diff = Math.abs(min) / BUCKETS_SIZE;

		let lower = min;
		let higher = min + diff;

		for (let index = 0; index < BUCKETS_SIZE; index++) {
			NEGATIVE_SLOTS.push([
				+(Math.round(lower * 100) / 100).toFixed(2),
				+(Math.round(higher * 100) / 100).toFixed(2),
			]);
			lower = lower + diff;
			higher = higher + diff;
		}
	}

	if (max > 0) {
		const diff = max / BUCKETS_SIZE;

		let lower = 0;
		let higher = diff;

		for (let index = 0; index < BUCKETS_SIZE; index++) {
			POSITIVE_SLOTS.push([
				+(Math.round(lower * 100) / 100).toFixed(2),
				+(Math.round(higher * 100) / 100).toFixed(2),
			]);
			lower = lower + diff;
			higher = higher + diff;
		}
	}

	if (min > 0 && max > 0) {
		NEGATIVE_SLOTS.length = 0;
		POSITIVE_SLOTS.length = 0;

		const diff = (max - min) / BUCKETS_SIZE;
		let lower = min;
		let higher = min + diff;

		for (let index = 0; index < BUCKETS_SIZE; index++) {
			POSITIVE_SLOTS.push([
				+(Math.round(lower * 100) / 100).toFixed(2),
				+(Math.round(higher * 100) / 100).toFixed(2),
			]);
			lower = lower + diff;
			higher = higher + diff;
		}
	}

	if (min < 0 && max < 0) {
		NEGATIVE_SLOTS.length = 0;
		POSITIVE_SLOTS.length = 0;

		const diff = Math.abs(max - min) / BUCKETS_SIZE;
		let lower = min;
		let higher = min + diff;

		for (let index = 0; index < BUCKETS_SIZE; index++) {
			NEGATIVE_SLOTS.push([
				+(Math.round(lower * 100) / 100).toFixed(2),
				+(Math.round(higher * 100) / 100).toFixed(2),
			]);
			lower = lower + diff;
			higher = higher + diff;
		}
	}

	return {
		POSITIVE_SLOTS,
		NEGATIVE_SLOTS,
	};
};

export const getTooltip = (isPercentageChange) => {
	const showPercentageChangeTooltip = isPercentageChange;
	return ({ object }) => {
		const style = {
			padding: '8px 16px 16px',
			display: INLINE_BLOCK,
			background: 'rgba(255, 255, 255, 0.90)',
			borderRadius: '4px',
			boxShadow:
				'0px 3px 5px 2px rgba(0, 0, 0, 0.15), 0px -1px 3px 0px rgba(0, 0, 0, 0.10)',
			width: '175px',
			height: 'auto',
			zIndex: 10000,
		};
		const getNoDataObj = () => {
			return {
				html: 'No data available!',
				style: {
					...style,
					padding: '24px 24px 24px 24px',
					width: '100px',
				},
			};
		};
		try {
			if (object) {
				const {
					properties: { yoyValue, tyValue, lyValue, movement, name, absYoY },
				} = object;

				const isUp = movement === 'up';
				const arrowIcon1 = isUp ? upArrowIcon : downArrowIcon;
				const fontColor1 = isUp
					? colors.POSITIVE_GROWTH_COLOR
					: colors.NEGATIVE_GROWTH_COLOR;
				const defaultFontColor = colors.FONT_COLOR;
				const fontColor2 =
					absYoY >= 0
						? colors.POSITIVE_GROWTH_COLOR
						: colors.NEGATIVE_GROWTH_COLOR;

				let content = '';
				if (showPercentageChangeTooltip) {
					content = `<div style="text-align:left;font-size:12px;">
					<span style="font-family:Bogle-Regular;font-size:14px;color:black;font-weight:700;display:inline-block">${name}</span>
					<div style="margin-top:8px;text-align:right; display:grid;grid-template-columns: auto auto auto;gap:8px">
							<div style="font-family:Bogle-regular;font-weight:400;font-size:12px;color:${defaultFontColor}">TY</div>
							<div style="font-family:Bogle-regular;font-weight:400;font-size:12px;color:${defaultFontColor}">LY</div>							
							<div style="font-family:Bogle-regular;font-weight:400;font-size:12px;color:${defaultFontColor}">% Chg</div>
						<div style="font-family:Bogle-medium;font-weight:500;font-size:12px;color:${defaultFontColor}"> ${formatNumberWithUnits(
						tyValue,
						'$'
					)}</div>
					<div style="font-family:Bogle-medium;font-weight:500;font-size:12px;color:${defaultFontColor}"> ${formatNumberWithUnits(
						lyValue,
						'$'
					)}</div>
						<div style="font-family:Bogle-medium;font-weight:500;font-size:12px;color:${fontColor1}"><img src="${arrowIcon1}"/>${formatNumberWithUnits(
						Math.abs(yoyValue),
						'',
						'%'
					)}</div>
					</div>
				</div>`;
				} else {
					content = `<div style="text-align:left;font-size:12px;">
						<span style="font-family:Bogle-Regular;font-size:14px;color:black;font-weight:700;display:inline-block">${name}</span>
						<div style="margin-top:8px;text-align:right; display:grid;grid-template-columns: auto auto auto;gap:8px">
								<div style="font-family:Bogle-regular;font-weight:400;font-size:12px;color:${defaultFontColor}">TY</div>
								<div style="font-family:Bogle-regular;font-weight:400;font-size:12px;color:${defaultFontColor}">LY</div>
								<div style="font-family:Bogle-regular;font-weight:400;font-size:12px;color:${defaultFontColor}">$ Chg</div>
							<div style="font-family:Bogle-medium;font-weight:500;font-size:12px;color:${defaultFontColor}">${formatNumberWithUnits(
						tyValue,
						'$'
					)}</div>
					<div style="font-family:Bogle-medium;font-weight:500;font-size:12px;color:${defaultFontColor}">${formatNumberWithUnits(
						lyValue,
						'$'
					)}</div>
							<div style="font-family:Bogle-medium;font-weight:500;font-size:12px;color:${fontColor2}">${formatNumberWithUnits(
						absYoY,
						'$'
					)}</div>
						</div>
					</div>`;
				}

				return {
					html: content,
					style,
				};
			}
		} catch (error) {
			return getNoDataObj();
		}
	};
};

export const getExcelDownloadData = (input) => {
	if (!input?.stateGeoJSONData?.features) {
		return [];
	}
	return input?.stateGeoJSONData?.features?.map((item) => ({
		...item.properties,
		absYoY: item.properties.absYoY == -999999 ? 'NA' : item.properties.absYoY,
	}));
};
export const formatNetSales = (netSales) => {
	return netSales < 0
		? `-$${convertToInternationalCurrencySystem(Math.abs(netSales))}`
		: `$${convertToInternationalCurrencySystem(netSales)}`;
};
